import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import FollowUpCoordinatorDirectoryTable from '@/tables/FollowUpCoordinatorDirectoryTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-coordinators/')({
  component: FollowUpCoordinatorsScreen,
});

/**
 * A follow-up partner's All Coordinators tab.
 *
 * A top-level route, not a child of `/follow-ups`, whose shell would paint its
 * two-tab strip with neither tab active. No Add button, as in legacy: a partner
 * can't create a coordinator, and the relation is written when the partner
 * accepts a follow-up. One title, where legacy also showed the group name,
 * since the follow-up groups are gone.
 */
function FollowUpCoordinatorsScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();

  // Partners only, narrower than the follow-up network shell: the endpoint
  // refuses everyone else, admins included, as legacy's tab did.
  // `me.profile_type`, not `useCurrentUser().isStudyHelper` (which includes
  // every non-student type) or `useIsCoordinator` (which needs a group slug and
  // never resolves on a top-level route).
  //
  // Defence in depth over the endpoint's own check. `isPending` first, or the
  // partner bounces themselves while `/users/me` loads.
  const canView = me?.profile_type === 'FOLLOWUP_PARTNER';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.all-coordinators')}
          placement="follow_up_coordinators"
        />
      </GroupHeaderWrapper>
      <FollowUpCoordinatorDirectoryTable />
    </PageContainer>
  );
}
